import type { TFunction, i18n as I18nInstance } from 'i18next'
import type { ParkingFeeLine } from '@/types/parking'
import { formatBaht } from '@/lib/money'

/**
 * One rendered row of the parking-fee table. `label` is the row qualifier
 * ("All vehicles" / "Motorcycle" / "≤6h"), `amount` is the formatted "฿X" string.
 * The unit suffix ("per day" / "per hour") is concatenated on the right.
 */
export interface RenderedFeeRow {
  label: string
  amount: string
}

/**
 * Render a single fee-formula line into the {label, amount} row used by every
 * parking-fee table on the web (operator sticker card, public check-in page,
 * pay-page fee block — all share this). Vehicle-scoped lines fall back to the
 * raw `vehicle_type` value when no i18n key exists, so unknown types render
 * verbatim rather than blanking out.
 *
 * EP-105 hourly tiers render as `≤Nh` / `฿X` with no per-day/per-hour suffix
 * (the bound itself communicates the unit). Daily / per_vehicle_type lines
 * render with the unit suffix.
 */
export function feeRowFor(
  line: ParkingFeeLine,
  t: TFunction,
  i18n: I18nInstance,
): RenderedFeeRow {
  const NS = 'parking'

  // Hourly tier line: `≤Nh` label, no unit suffix.
  if (line.kind === 'hourly') {
    const matchedHours = Math.trunc(Number(line.max_hours))
    return {
      label: t('zones.public_checkin.fee_hourly_tier', {
        ns: NS,
        hours: matchedHours,
      }),
      amount: `฿${formatBaht(Number(line.amount), { withSymbol: false })}`,
    }
  }

  const vehicleKey = `vehicles.types.${line.vehicle_type}`
  const label = line.vehicle_type
    ? i18n.exists(vehicleKey, { ns: NS })
      ? t(vehicleKey, { ns: NS })
      : line.vehicle_type
    : t('zones.public_checkin.fee_all_vehicles', { ns: NS })
  const per = t('zones.public_checkin.fee_daily', { ns: NS })
  return { label, amount: `฿${formatBaht(Number(line.amount), { withSymbol: false })} ${per}` }
}

/**
 * Encode a fee formula as a URL-safe base64 JSON blob to append as `&fee=…`.
 * Used by the sticker card's "Copy link" so the public check-in page can render
 * the fee without needing an extra API round-trip. Returns null when the
 * formula is empty (the check-in page then skips the table).
 */
export function encodeFeeParam(formula: ParkingFeeLine[] | null | undefined): string | null {
  if (!formula || formula.length === 0) return null
  // Strip extra fields the engine doesn't read — keeps the URL short and
  // avoids leaking server-side metadata into the QR sticker.
  const compact = formula.map((l) => {
    switch (l.kind) {
      case 'hourly':
        return { kind: 'hourly', max_hours: Number(l.max_hours), amount: Number(l.amount) }
      case 'per_vehicle_type':
        return { kind: 'per_vehicle_type', amount: Number(l.amount), vehicle_type: l.vehicle_type }
      case 'daily':
      default:
        return {
          kind: 'daily',
          amount: Number(l.amount),
          ...(l.vehicle_type ? { vehicle_type: l.vehicle_type } : {}),
        }
    }
  })
  const json = JSON.stringify(compact)
  const b64 = btoa(unescape(encodeURIComponent(json)))
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Normalize an already-parsed fee formula (from a `?fee=` param OR the kiosk's
 * apikey-gated GET) into a well-typed `ParkingFeeLine[]`. Tolerant of malformed
 * input — returns null for anything that isn't a non-empty array of
 * {kind, amount} objects, so the caller can simply skip the table. Numeric
 * fields may arrive as JSON-string (server-side JSONB serialization quirk for
 * the max_hours/amount columns) — coerced through Number() on the way out.
 */
export function normalizeFeeFormula(parsed: unknown): ParkingFeeLine[] | null {
  if (!Array.isArray(parsed) || parsed.length === 0) return null
  const ok = parsed.every((l) => {
    if (l == null || typeof l !== 'object') return false
    if (l.kind === 'hourly') {
      return Number.isFinite(Number(l.amount)) && Number.isFinite(Number(l.max_hours))
    }
    return (
      (l.kind === 'daily' || l.kind === 'per_vehicle_type') &&
      Number.isFinite(Number(l.amount))
    )
  })
  if (!ok) return null
  return parsed.map((l): ParkingFeeLine => {
    switch (l.kind) {
      case 'hourly':
        return { kind: 'hourly', max_hours: Number(l.max_hours), amount: Number(l.amount) }
      case 'per_vehicle_type':
        return {
          kind: 'per_vehicle_type',
          amount: Number(l.amount),
          vehicle_type: String(l.vehicle_type ?? ''),
        }
      case 'daily':
      default:
        return {
          kind: 'daily',
          amount: Number(l.amount),
          ...(l.vehicle_type ? { vehicle_type: String(l.vehicle_type) } : {}),
        }
    }
  })
}

/**
 * Decode a `?fee=` search-param value back into a fee formula. Base64url →
 * JSON → {@link normalizeFeeFormula}. Returns null on any malformed input.
 */
export function decodeFeeParam(raw: string | null | undefined): ParkingFeeLine[] | null {
  if (!raw) return null
  try {
    const padded = raw.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((raw.length + 3) % 4)
    const json = decodeURIComponent(escape(atob(padded)))
    return normalizeFeeFormula(JSON.parse(json))
  } catch {
    return null
  }
}
